/**
 * ElapsedTimer Component
 *
 * Glitch-free elapsed timer for the TradingLayout header clockTimerSlot.
 * Displays accumulated phase time in HH:MM:SS with optional visibility toggle.
 *
 * Timer isolation:
 * - Tick state uses useRef (synced at render time, not in effects) so the
 *   shared useSecondPulse callback always reads current values.
 * - Only the formatted display string triggers re-renders, contained within
 *   this React.memo boundary. Sibling components are unaffected.
 *
 * Visibility toggle:
 * - Pressing the Timer icon hides/shows the HH:MM:SS text.
 * - Preference persists in localStorage; timer continues accumulating
 *   in the background regardless of display state.
 *
 * @see CODING_STANDARDS §2.2 — Polling with React Strict Mode (useRef pattern)
 */

// 1. External dependencies
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Timer } from 'lucide-react';

// 2. Internal absolute imports — hooks
import { useSecondPulse } from '@/hooks';

// 2. Internal absolute imports
import { Brand } from '@enfomentis/shared/config';

// ===== CONSTANTS =====

/** localStorage key for timer visibility preference */
const STORAGE_KEY = `${Brand.cachePrefix}-tradinghub-timer-visible`;

// ===== HELPERS =====

/** Format milliseconds as HH:MM:SS */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/** Compute total elapsed ms from accumulated base + active stint */
function computeElapsed(base: number, startMs: number | null, now?: number): number {
  if (startMs === null || !Number.isFinite(startMs)) return base;
  return base + Math.max(0, (now ?? Date.now()) - startMs);
}

/** Parse ISO timestamp to epoch ms, or null if absent */
function parseStartMs(iso: string | undefined): number | null {
  return iso ? new Date(iso).getTime() : null;
}

/** Read timer visibility from localStorage with safe default (visible) */
function getStoredVisibility(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return true;
    return JSON.parse(stored) === true;
  } catch {
    return true;
  }
}

// ===== COMPONENT =====

interface ElapsedTimerProps {
  /** Accumulated ms for the current phase from previous stints */
  phaseElapsedMs: number;
  /** ISO timestamp of when the current phase stint began (undefined if no active stint) */
  currentPhaseStartedAt: string | undefined;
}

/**
 * Elapsed Timer — header clockTimerSlot content.
 *
 * Ref-based ticking guarantees monotonic display within a phase stint:
 * Date.now() is monotonically increasing and baseRef is stable within a stint,
 * so the computed elapsed value never decreases mid-phase.
 */
export const ElapsedTimer: React.FC<ElapsedTimerProps> = React.memo(({
  phaseElapsedMs,
  currentPhaseStartedAt,
}) => {
  const [display, setDisplay] = useState<string>(
    () => formatElapsed(computeElapsed(phaseElapsedMs, parseStartMs(currentPhaseStartedAt))),
  );
  const [isVisible, setIsVisible] = useState<boolean>(getStoredVisibility);

  // Refs for stable interval access — synced during render body (not in effects)
  // per CODING_STANDARDS §2.2
  const baseRef = useRef(phaseElapsedMs);
  const startMsRef = useRef<number | null>(null);

  baseRef.current = phaseElapsedMs;
  startMsRef.current = parseStartMs(currentPhaseStartedAt);

  // Immediate display on mount — no blank first render
  useEffect(() => {
    setDisplay(formatElapsed(computeElapsed(baseRef.current, startMsRef.current)));
  }, []);

  // Second-boundary aligned ticks via shared pulse (CODING_STANDARDS §2.2)
  useSecondPulse((tickAtMs) => {
    setDisplay(formatElapsed(computeElapsed(baseRef.current, startMsRef.current, tickAtMs)));
  });

  // Immediate display update on phase transition — prevents up to 1s stale display
  // when props change between interval ticks.
  useEffect(() => {
    setDisplay(formatElapsed(computeElapsed(phaseElapsedMs, parseStartMs(currentPhaseStartedAt))));
  }, [phaseElapsedMs, currentPhaseStartedAt]);

  const toggleVisibility = useCallback((): void => {
    setIsVisible((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Storage full — non-critical
      }
      return next;
    });
  }, []);

  return (
    <button
      type="button"
      onClick={toggleVisibility}
      className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity"
      aria-label={isVisible ? 'Hide elapsed timer' : 'Show elapsed timer'}
    >
      <Timer size={14} className="text-text-secondary" />
      {isVisible && (
        <span className="text-xs font-mono font-medium text-text-primary tabular-nums">
          {display}
        </span>
      )}
    </button>
  );
});

ElapsedTimer.displayName = 'ElapsedTimer';
